import {
  Component,
  EventEmitter,
  Input,
  inject,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { webSocket, type WebSocketSubjectConfig } from 'rxjs/webSocket';
import {
  WebSocketMessage,
  WebSocketSubject,
} from 'rxjs/internal/observable/dom/WebSocketSubject';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { EnvironmentService } from '../../services/environment.service';

@Component({
  selector: 'app-speech-to-text',
  imports: [MatButtonModule, MatIconModule, MatCheckboxModule],
  templateUrl: './speech-to-text.component.html',
  styleUrl: './speech-to-text.component.scss',
})
export class SpeechToTextComponent implements OnInit, OnChanges {
  private envSvc = inject(EnvironmentService);

  @Input() typedText: string;
  @Input() speechReviewed: boolean;
  @Input() externalTriggerStop: any;
  @Output() text = new EventEmitter<string>();
  @Output() noteSpeechError = new EventEmitter<string>();
  micButtonStatus = 'disabled';
  isListening = false;
  newSpokenText = false;
  noteError = false;
  rawError: { code: number; reason: string } | null = null;
  recognition;
  spokenText: string;
  subject: WebSocketSubject<{
    info: string;
    partial: string;
    text: string;
  }> | null = null;
  audioWorkletNode: AudioWorkletNode | null = null;
  audioContext: AudioContext | null = null;
  source: MediaStreamAudioSourceNode | null = null;
  stream: MediaStream | null = null;
  lastPartialLen = 0;
  ariaButtonActivity = 'Toggle speech-to-text, currently disabled';
  aiUrl = '';

  // Centralizes socket creation so tests can replace the transport before audio starts.
  createSocket(
    config: WebSocketSubjectConfig<{
      info: string;
      partial: string;
      text: string;
    }>
  ): WebSocketSubject<{ info: string; partial: string; text: string }> {
    return webSocket<{ info: string; partial: string; text: string }>(config);
  }

  ngOnInit(): void {
    this.spokenText = this.typedText || '';
    const env = this.envSvc.get();

    if (env?.env) {
      this.aiUrl = `${env.aiBase}/transcribe`;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.typedText &&
      typeof changes.typedText.currentValue === 'string'
    ) {
      this.spokenText = changes.typedText.currentValue;
    }
    if (
      changes.speechReviewed &&
      changes.speechReviewed.currentValue === true &&
      this.source?.mediaStream?.active
    ) {
      this.lastPartialLen = 0;
      this.subject.complete();
    }
    if (
      changes.externalTriggerStop &&
      changes.externalTriggerStop.currentValue &&
      this.source?.mediaStream?.active
    ) {
      this.lastPartialLen = 0;
      this.subject.complete();
    }
  }

  private cleanupResources(): void {
    if (this.source) this.source.disconnect();
    if (this.audioWorkletNode) this.audioWorkletNode.disconnect();
    if (this.stream) this.stream.getTracks().forEach(t => t.stop());
    if (this.audioContext) this.audioContext.close();
    this.micButtonStatus = 'disabled';
    this.ariaButtonActivity = 'Toggle speech-to-text, currently disabled';
  }

  private parsePartial(msg): void {
    // Remove old partial and append new partial
    try {
      const p = msg.partial;
      this.spokenText =
        this.spokenText.slice(0, this.spokenText.length - this.lastPartialLen) +
        p;
      this.lastPartialLen = p.length;

      this.text.emit(this.spokenText);
    } catch (error) {
      this.rawError = error;
      this.noteSpeechError.emit(
        'An unexpected error occurred while transcribing audio.'
      );
      this.subject.error({ code: 4000, reason: error });
    }
  }
  private parseComplete(msg): void {
    // Remove remaining interim and append final text
    try {
      const f = msg.text + ' ';
      this.spokenText =
        this.spokenText.slice(0, this.spokenText.length - this.lastPartialLen) +
        f;
      this.lastPartialLen = 0;
      this.subject.complete();

      // Emit the compiled text
      this.text.emit(this.spokenText);
    } catch (error) {
      this.rawError = error;
      this.noteSpeechError.emit(
        'An unexpected error occurred while transcribing audio.'
      );
      this.subject.error({ code: 4000, reason: error });
    }
  }

  async startListening() {
    if (!this.source?.mediaStream?.active) {
      this.rawError = null;
      this.noteSpeechError.emit('reset');
      this.micButtonStatus = 'primary';
      this.ariaButtonActivity = 'Toggle speech-to-text, currently enabled';
      this.subject = this.createSocket({
        url: this.aiUrl,
        serializer: (msg: unknown): WebSocketMessage =>
          msg as WebSocketMessage,
      });

      this.subject.subscribe({
        next: msg => {
          if (msg.partial !== undefined) {
            this.parsePartial(msg);
          }

          if (msg.text !== undefined) {
            this.parseComplete(msg);
          }
        }, // Called whenever there is a message from the server.
        error: err => {
          this.rawError = err;
          this.noteSpeechError.emit('An unexpected error has occurred.');
          this.subject.unsubscribe();
          this.cleanupResources();
        }, // Called if at any point WebSocket API signals some kind of error.
        complete: () => {
          this.cleanupResources();
        }, // Called when connection is closed (for whatever reason).
      });

      try {
        this.audioContext = new AudioContext({ sampleRate: 16000 });
        await this.audioContext.resume();
        // Request access to the user's microphone
        this.stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });

        // Create a MediaStreamSource from the microphone stream
        this.source = this.audioContext.createMediaStreamSource(this.stream);

        const workletCode = `
          class PCMProcessor extends AudioWorkletProcessor {
            process(inputs) {
              const input = inputs[0][0];
              if (input) {
                const pcm = new Int16Array(input.length);
                for (let i = 0; i < input.length; i++) {
                  let s = Math.max(-1, Math.min(1, input[i]));
                  pcm[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
                }
                this.port.postMessage(pcm.buffer, [pcm.buffer]);
              }
              return true;
            }
          }
          registerProcessor('pcm-processor', PCMProcessor);
        `;

        const blob = new Blob([workletCode], {
          type: 'application/javascript',
        });
        const url = URL.createObjectURL(blob);

        // Load the audio worklet processor
        await this.audioContext.audioWorklet.addModule(url);

        // Create an AudioWorkletNode
        this.audioWorkletNode = new AudioWorkletNode(
          this.audioContext,
          'pcm-processor'
        );

        // Listen for messages from the AudioWorkletNode
        this.audioWorkletNode.port.onmessage = event => {
          const int16Data = event.data; // Processed Int16Array audio data
          this.subject.next(int16Data); // Send audio data to WebSocket
        };

        this.source
          .connect(this.audioWorkletNode)
          .connect(this.audioContext.destination);
      } catch {
        const microphoneError = {
          code: 4001,
          reason: 'Error accessing microphone.',
        };
        this.rawError = microphoneError;
        this.noteSpeechError.emit(
          'The audio transcription service could not access the microphone. Please check the device and/or permissions.'
        );
        this.subject.error(microphoneError);
      }
    } else {
      this.lastPartialLen = 0;
      this.subject.complete();
    }
  }
}
